import { Box, Button, Stack, Typography } from "@mui/material";
import { ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AsyncMultiPicker,
  ConfirmDialog,
  HistoryTimeline,
  InlineAlert,
  Loader,
  LoadingButton,
  SideDrawer,
  SourceBadge,
  StatusChip,
  StudentRecordForm,
} from "src/grading/components";
import {
  messageOf,
  refusalCode,
  refusalCopy,
} from "src/grading/utils/apiError";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { HistoryPageArgs } from "src/models/history";
import { StudentClassRef, studentName } from "src/models/students";
import {
  useDeactivateStudentMutation,
  useEnrollStudentMutation,
  useGetMyClassesQuery,
  useGetStudentHistoryQuery,
  useGetStudentQuery,
  useMoveStudentMutation,
  useReactivateStudentMutation,
  useWithdrawStudentMutation,
} from "src/store/reducers/classes/classesApiSlice";
import StudentClassSelect from "./StudentClassSelect";
import usePeopleScope from "./usePeopleScope";
import {
  StudentClassOption,
  useStudentClassOptions,
} from "./useStudentClassOptions";

export interface StudentPanelProps {
  studentId: string;
  onClose: () => void;
  /** Teachers pick from their own classes and withdraw only from them. */
  ownClasses: boolean;
  /** Platform admins view students without changing them. */
  readOnly?: boolean;
  /** The platform admin's chosen district, for class searches. */
  districtId?: string;
}

type Notice = { severity: "success" | "error"; text: string };

const Section = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) => (
  <Box component="section" aria-labelledby={id} sx={{ mb: 3 }}>
    <Typography id={id} variant="h4" component="h3" sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/** One student: Details (the record form), Classes and History, with the actions the server allows. */
export const StudentPanel = ({
  studentId,
  onClose,
  ownClasses,
  readOnly = false,
  districtId,
}: StudentPanelProps) => {
  const { t } = useTranslation();
  const uid = useId();
  const scope = usePeopleScope(districtId);
  const {
    data: student,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetStudentQuery(studentId);
  const { data: myClasses } = useGetMyClassesQuery(undefined, {
    skip: !ownClasses,
  });
  const [enrollStudent, { isLoading: enrolling }] = useEnrollStudentMutation();
  const [withdrawStudent, { isLoading: withdrawing }] =
    useWithdrawStudentMutation();
  const [moveStudent, { isLoading: moving }] = useMoveStudentMutation();
  const [deactivateStudent, { isLoading: deactivating }] =
    useDeactivateStudentMutation();
  const [reactivateStudent, { isLoading: reactivating }] =
    useReactivateStudentMutation();

  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<StudentClassOption[]>([]);
  const [removing, setRemoving] = useState<StudentClassRef | null>(null);
  const [movingFrom, setMovingFrom] = useState<StudentClassRef | null>(null);
  const [moveTo, setMoveTo] = useState<StudentClassOption | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [reactivated, setReactivated] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [classesNotice, setClassesNotice] = useState<Notice | null>(null);

  const errorText = (err: unknown, fallback: string) => {
    const code = refusalCode(err);
    return code ? refusalCopy(t, code) : messageOf(err, fallback);
  };

  const name = student ? studentName(student) : "";
  const allowed = student?.allowedActions;
  const canReadHistory = scope.has(permissions.READ_AUDIT);
  const useHistoryPage = (page: Required<HistoryPageArgs>) =>
    useGetStudentHistoryQuery({ studentId, ...page });
  const enrolledIds = (student?.classes ?? []).map((c) => c.id);
  const myClassIds = myClasses?.map((c) => c.id);
  const classArgs = {
    ownClasses,
    districtId: scope.requestDistrictId,
    schoolId: student?.schoolId,
  };
  const useSearch = (term: string) =>
    useStudentClassOptions({ ...classArgs, term, exclude: enrolledIds });

  const canWithdrawFrom = (schoolClass: StudentClassRef) =>
    !readOnly &&
    Boolean(allowed?.withdraw) &&
    schoolClass.source === "manual" &&
    (!ownClasses || !myClassIds || myClassIds.includes(schoolClass.id));
  const canMoveFrom = (schoolClass: StudentClassRef) =>
    !readOnly &&
    !ownClasses &&
    Boolean(allowed?.move) &&
    schoolClass.source === "manual";
  const hasRosterClass = (student?.classes ?? []).some(
    (c) => c.source === "roster",
  );

  const addToClasses = async () => {
    setClassesNotice(null);
    let added = 0;
    try {
      for (const option of picked) {
        await enrollStudent({ studentId, classId: option.id }).unwrap();
        added += 1;
      }
      setPicked([]);
      setAdding(false);
      setReactivated(false);
      setClassesNotice({
        severity: "success",
        text: t("people.students.panel.added", { count: added }),
      });
    } catch (err) {
      setPicked((current) => current.slice(added));
      setClassesNotice({
        severity: "error",
        text: errorText(err, t("student.add.enrollError")),
      });
    }
  };

  const withdraw = async () => {
    if (!removing) return;
    setClassesNotice(null);
    try {
      await withdrawStudent({ studentId, classId: removing.id }).unwrap();
      setClassesNotice({
        severity: "success",
        text: t("student.removed", { name, className: removing.name }),
      });
    } catch (err) {
      setClassesNotice({
        severity: "error",
        text: errorText(err, t("student.removeError")),
      });
    }
    setRemoving(null);
  };

  const move = async () => {
    if (!movingFrom || !moveTo) return;
    setClassesNotice(null);
    try {
      await moveStudent({
        studentId,
        fromClassId: movingFrom.id,
        toClassId: moveTo.id,
      }).unwrap();
      setClassesNotice({
        severity: "success",
        text: t("people.students.panel.moved", { className: moveTo.name }),
      });
      setMovingFrom(null);
      setMoveTo(null);
    } catch (err) {
      setClassesNotice({
        severity: "error",
        text: errorText(err, t("people.students.panel.moveError")),
      });
    }
  };

  const deactivate = async () => {
    setNotice(null);
    try {
      await deactivateStudent({ studentId }).unwrap();
      setNotice({
        severity: "success",
        text: t("people.students.panel.deactivated", { name }),
      });
    } catch (err) {
      setNotice({
        severity: "error",
        text: errorText(err, t("people.students.panel.actionError")),
      });
    }
    setConfirmDeactivate(false);
  };

  const reactivate = async () => {
    setNotice(null);
    try {
      await reactivateStudent(studentId).unwrap();
      setReactivated(true);
      setNotice({
        severity: "success",
        text: t("people.students.panel.reactivated", { name }),
      });
    } catch (err) {
      setNotice({
        severity: "error",
        text: errorText(err, t("people.students.panel.actionError")),
      });
    }
  };

  const showDeactivate = !readOnly && Boolean(allowed?.deactivate);
  const showReactivate = !readOnly && Boolean(allowed?.reactivate);
  const footer =
    student && (showDeactivate || showReactivate) ? (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {showReactivate ? (
          <LoadingButton
            variant="outlined"
            loading={reactivating}
            onClick={reactivate}
            label={t("people.students.panel.reactivate")}
          />
        ) : null}
        {showDeactivate ? (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirmDeactivate(true)}
            sx={{ ml: "auto" }}
          >
            {t("people.students.panel.deactivate")}
          </Button>
        ) : null}
      </Stack>
    ) : undefined;

  const body = (() => {
    if (isLoading) return <Loader />;
    if (isError || !student) {
      return (
        <InlineAlert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {t("common.retry")}
            </Button>
          }
        >
          {messageOf(error, t("people.students.panel.loadError"))}
        </InlineAlert>
      );
    }
    const canAdd = !readOnly && Boolean(allowed?.enroll);
    return (
      <>
        {readOnly ? (
          <Box sx={{ mb: 2 }}>
            <InlineAlert severity="info">
              {t("people.students.panel.readOnly")}
            </InlineAlert>
          </Box>
        ) : null}
        {notice ? (
          <Box sx={{ mb: 2 }}>
            <InlineAlert severity={notice.severity}>{notice.text}</InlineAlert>
          </Box>
        ) : null}

        <Section
          id={`${uid}-details`}
          title={t("people.students.panel.details")}
        >
          <StudentRecordForm
            student={student}
            readOnly={readOnly}
            schools={ownClasses ? undefined : scope.schools}
            onReload={() => refetch()}
          />
        </Section>

        <Section
          id={`${uid}-classes`}
          title={t("people.students.panel.classes")}
        >
          <Stack spacing={1.5}>
            {student.classes.length === 0 ? (
              <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                {t("people.students.panel.noClassesYet")}
              </Typography>
            ) : (
              <Stack
                component="ul"
                aria-label={t("people.students.panel.classes")}
                spacing={1}
                sx={{ m: 0, p: 0, listStyle: "none" }}
              >
                {student.classes.map((schoolClass) => (
                  <Box
                    component="li"
                    key={schoolClass.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ fontWeight: 700 }}>{schoolClass.name}</Box>
                      <SourceBadge source={schoolClass.source} />
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      {canMoveFrom(schoolClass) ? (
                        <Button
                          size="small"
                          aria-label={t("people.students.panel.moveLabel", {
                            name,
                            className: schoolClass.name,
                          })}
                          onClick={() => {
                            setMoveTo(null);
                            setMovingFrom(schoolClass);
                          }}
                        >
                          {t("people.students.panel.move")}
                        </Button>
                      ) : null}
                      {canWithdrawFrom(schoolClass) ? (
                        <Button
                          size="small"
                          color="error"
                          aria-label={t("student.removeFromNamedClass", {
                            name,
                            className: schoolClass.name,
                          })}
                          onClick={() => setRemoving(schoolClass)}
                        >
                          {t("student.removeFromClass")}
                        </Button>
                      ) : null}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
            {hasRosterClass && !readOnly ? (
              <Typography
                sx={(theme) => ({
                  fontSize: 13,
                  color: theme.palette.tokens.textMuted,
                })}
              >
                {t("student.rosterManaged")}
              </Typography>
            ) : null}
            {reactivated && student.classes.length === 0 && canAdd ? (
              <Box
                component="section"
                aria-labelledby={`${uid}-enroll-prompt`}
                sx={(theme) => ({
                  p: 2,
                  borderRadius: 2,
                  bgcolor: theme.palette.surfaceAlt,
                })}
              >
                <Typography
                  id={`${uid}-enroll-prompt`}
                  component="h4"
                  sx={{ fontWeight: 800, fontSize: 15, mb: 0.5 }}
                >
                  {t("people.students.panel.enrollPromptTitle", { name })}
                </Typography>
                <Typography sx={{ fontSize: 14 }}>
                  {t("people.students.panel.enrollPrompt", { name })}
                </Typography>
              </Box>
            ) : null}
            {canAdd ? (
              adding ? (
                <Stack spacing={1.5}>
                  <AsyncMultiPicker<StudentClassOption>
                    label={t("people.students.panel.classesToAdd")}
                    useSearch={useSearch}
                    getOptionLabel={(option) => option.name}
                    getOptionKey={(option) => option.id}
                    renderOptionExtra={(option) => option.schoolName ?? ""}
                    value={picked}
                    onChange={setPicked}
                  />
                  <Stack direction="row" spacing={1}>
                    <LoadingButton
                      variant="contained"
                      loading={enrolling}
                      disabled={picked.length === 0}
                      onClick={addToClasses}
                      label={t("people.students.panel.addSubmit")}
                    />
                    <Button
                      color="inherit"
                      disabled={enrolling}
                      onClick={() => {
                        setAdding(false);
                        setPicked([]);
                      }}
                    >
                      {t("common.cancel")}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Box>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setClassesNotice(null);
                      setAdding(true);
                    }}
                  >
                    {t("people.students.panel.addToClass")}
                  </Button>
                </Box>
              )
            ) : null}
            {movingFrom ? (
              <Box
                component="section"
                aria-labelledby={`${uid}-move`}
                sx={(theme) => ({
                  p: 2,
                  borderRadius: 2,
                  bgcolor: theme.palette.surfaceAlt,
                })}
              >
                <Typography
                  id={`${uid}-move`}
                  component="h4"
                  sx={{ fontWeight: 800, fontSize: 15, mb: 1.5 }}
                >
                  {t("people.students.panel.moveTitle", {
                    name,
                    className: movingFrom.name,
                  })}
                </Typography>
                <StudentClassSelect
                  {...classArgs}
                  manualOnly
                  exclude={enrolledIds}
                  label={t("people.students.panel.moveTo")}
                  value={moveTo}
                  onChange={setMoveTo}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <LoadingButton
                    variant="contained"
                    loading={moving}
                    disabled={!moveTo}
                    onClick={move}
                    label={t("people.students.panel.moveSubmit")}
                  />
                  <Button
                    color="inherit"
                    disabled={moving}
                    onClick={() => setMovingFrom(null)}
                  >
                    {t("common.cancel")}
                  </Button>
                </Stack>
              </Box>
            ) : null}
            {classesNotice ? (
              <InlineAlert severity={classesNotice.severity}>
                {classesNotice.text}
              </InlineAlert>
            ) : null}
          </Stack>
        </Section>

        {canReadHistory ? (
          <Section id={`${uid}-history`} title={t("history.title")}>
            <HistoryTimeline subjectName={name} usePage={useHistoryPage} />
          </Section>
        ) : null}
      </>
    );
  })();

  return (
    <>
      <SideDrawer
        open
        onClose={onClose}
        width={480}
        title={name || t("common.loading")}
        subtitle={
          student ? (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {student.externalId ? (
                <span>
                  {t("people.students.sisLine", { id: student.externalId })}
                </span>
              ) : null}
              <SourceBadge source={student.source} />
              <StatusChip status={student.active ? "active" : "deactivated"} />
            </Box>
          ) : undefined
        }
        footer={footer}
      >
        {body}
      </SideDrawer>
      {student ? (
        <>
          <ConfirmDialog
            open={confirmDeactivate}
            tone="destructive"
            title={t("people.students.panel.deactivateTitle", { name })}
            body={t("people.students.panel.deactivateBody", { name })}
            confirmLabel={t("people.students.panel.deactivate")}
            loading={deactivating}
            onConfirm={deactivate}
            onClose={() => setConfirmDeactivate(false)}
          />
          <ConfirmDialog
            open={!!removing}
            tone="destructive"
            title={t("student.removeTitle", {
              name,
              className: removing?.name ?? "",
            })}
            body={t("student.removeBody")}
            confirmLabel={t("student.removeFromClass")}
            loading={withdrawing}
            onConfirm={withdraw}
            onClose={() => setRemoving(null)}
          />
        </>
      ) : null}
    </>
  );
};

export default StudentPanel;
